<template>
  <template v-if="!isMounted && $slots.trigger">
    <slot name="trigger" />
  </template>
  <UiModal
    v-if="isMounted && isModal"
    ref="modal"
    :modal-id="modalId"
    :modal-focus="modalFocus"
    :render-if-hidden="false"
    :close-with-history-back="props.closeWithHistoryBack"
    :is-daterange-picker="props.isDaterangePicker"
    @shown="modalOrDropdownShown"
    @hidden="modalOrDropdownHidden"
  >
    <template v-if="$slots.trigger" #trigger>
      <slot name="trigger" />
    </template>
    <template #head>
      <slot v-if="$slots.head" name="head" />
    </template>
    <template #default>
      <slot name="default" />
    </template>
    <template v-if="$slots.footerClose" #footerClose>
      <slot name="footerClose" />
    </template>
    <template v-if="$slots.foot" #foot>
      <slot name="foot" />
    </template>
  </UiModal>
  <UiDropdown
    v-if="isMounted && !isModal"
    ref="dropdown"
    :custom-design-head="props.designHead"
    :custom-design="props.design"
    :custom-design-footer="props.customDesignFooter"
    :reduce-height="props.reduceHeightDropdown"
    :is-daterange-picker="props.isDaterangePicker"
    @update:is-open="updateDropdownState"
    @shown="modalOrDropdownShown"
    @hidden="modalOrDropdownHidden"
  >
    <template v-if="$slots.trigger" #trigger>
      <slot name="trigger" />
    </template>
    <template v-if="$slots.head" #head>
      <slot name="head" />
    </template>
    <template #default>
      <slot name="default" />
    </template>
    <template v-if="$slots.footerClose" #footerClose>
      <slot name="footerClose" />
    </template>
    <template v-if="$slots.foot" #foot>
      <slot name="foot" />
    </template>
  </UiDropdown>
</template>

<script setup lang="ts">
const emit = defineEmits(['shown', 'hidden', 'update:isOpen'])

const props = defineProps({
  breakpoint: {
    type: Number,
    default: 768
  },
  design: {
    type: String,
    default: ''
  },
  designHead: {
    type: String,
    default: ''
  },
  customDesignFooter: {
    type: String,
    default: ''
  },
  reduceHeightDropdown: {
    type: [Number, String],
    default: 100
  },
  modalFocus: {
    type: Boolean,
    default: true
  },
  isDaterangePicker: {
    type: Boolean,
    default: false
  },
  closeWithHistoryBack: {
    type: Boolean,
    default: true
  }
})

const isMounted = ref(false)
const modal = ref()
const dropdown = ref()

const modalId = useComponentId('modal-or-dropdown')

const show = () => {
  modal.value?.show()
  dropdown.value?.show()
}

const hide = (reason?: string) => {
  modal.value?.hide(reason)
  dropdown.value?.hide(reason)
}

const toggle = () => {
  modal.value?.show()
  dropdown.value?.toggle()
}

const modalOrDropdownShown = () => {
  emit('shown')
}

const modalOrDropdownHidden = (reason?: string) => {
  emit('hidden', reason)
}

const isModal = ref(true)
const isOpen = computed(() => Boolean(modal.value?.isOpen || dropdown.value?.isOpen))
const updateDropdownState = (newState) => {
  emit('update:isOpen', newState)
}

defineExpose({
  show,
  hide,
  toggle,
  isOpen,
  isModal
})

const listener = (event) => {
  isModal.value = event.matches
}

let mediaQuery
if (import.meta.client) {
  mediaQuery = window.matchMedia(`(max-width: ${props.breakpoint}px)`)
}

onMounted(() => {
  isModal.value = mediaQuery?.matches
  mediaQuery?.addEventListener('change', listener)
  isMounted.value = true
})

let popstateHandler: ((event: PopStateEvent) => void) | undefined

if (import.meta.client) {
  popstateHandler = () => {
    if (isOpen.value) {
      const inputElements = document.querySelectorAll('input')
      inputElements.forEach((input) => {
        input.blur()
      })
      hide('popstate')
      isModal.value = mediaQuery?.matches
      mediaQuery?.addEventListener('change', listener)
    }
  }

  onMounted(() => {
    window.addEventListener('popstate', popstateHandler!)
  })
}

onUnmounted(() => {
  mediaQuery?.removeEventListener('change', listener)
  if (popstateHandler) {
    window.removeEventListener('popstate', popstateHandler)
  }
})

</script>
<style>
.popper-position-w-full{
  width: 100% !important;
  margin: 0 !important;
  border: none !important;
}
</style>
